import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

const POSTER_FRAME =
  "w-[160px] overflow-hidden rounded-md sm:w-[152px] md:w-[180px] lg:w-[240px] aspect-[2/3]";

function NextArrowButton({ disabled, onPress }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      aria-label="Next shows"
      className="group shrink-0 self-center p-2 text-white transition disabled:cursor-not-allowed disabled:opacity-[0.28] enabled:hover:text-white enabled:hover:opacity-90 enabled:active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
    >
      <FaArrowRight className="h-4 w-4 sm:h-[18px] sm:w-[18px]" aria-hidden />
    </button>
  );
}

function updateNextDisabled(swiper, setNextDisabled) {
  if (!swiper) return;
  const locked = swiper.isLocked;
  setNextDisabled(locked || swiper.isEnd);
}

/**
 * Poster strip with Swiper: responsive spacing, touch/trackpad scroll, next arrow.
 * @param {string} [title]
 * @param {{ key?: string, image: string, to: string, alt?: string }[]} items
 */
export default function SelectShowPosterStrip({ title = "Select A Show", items = [], className = "" }) {
  const swiperRef = useRef(null);
  const [nextDisabled, setNextDisabled] = useState(true);

  const refreshNav = useCallback((swiper) => updateNextDisabled(swiper, setNextDisabled), []);

  const onSwiper = useCallback(
    (swiper) => {
      swiperRef.current = swiper;
      refreshNav(swiper);
    },
    [refreshNav]
  );

  if (!items.length) return null;

  return (
    <section
      className={`bg-ink text-white py-8 sm:py-10 px-4 sm:px-6 md:px-10 ${className}`}
      aria-label={title}
    >
      <div className="mx-auto max-w-[1400px]">
        <h2 className="font-sf-pro-display mb-4 text-xl font-semibold tracking-tight sm:text-2xl">
          {title}
        </h2>

        <div className="flex items-center gap-3 sm:gap-5 md:gap-6">
          <div className="min-w-0 flex-1 -mx-4 overflow-hidden sm:mx-0">
            <Swiper
              className="select-show-poster-strip !px-4 sm:!px-0"
              slidesPerView="auto"
              spaceBetween={12}
              watchOverflow
              breakpoints={{
                640: { spaceBetween: 16 },
              }}
              onSwiper={onSwiper}
              onSlideChange={refreshNav}
              onResize={refreshNav}
              onBreakpoint={refreshNav}
            >
              {items.map((item) => (
                <SwiperSlide key={item.key || item.to} className="!w-auto">
                  <Link
                    to={item.to}
                    className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                  >
                    <div className={POSTER_FRAME}>
                      <img
                        src={item.image}
                        alt={item.alt || "Show"}
                        className="h-full w-full object-cover object-center transition duration-300 ease-out group-hover:scale-[1.04] group-hover:brightness-110"
                        loading="lazy"
                      />
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <NextArrowButton
            disabled={nextDisabled}
            onPress={() => swiperRef.current?.slideNext()}
          />
        </div>
      </div>
    </section>
  );
}
